import { action, atom, batch } from '@reatom/framework'
import i18next from 'i18next'

import { Array, AsyncStateN, pipe, Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import {
  storeWalletRequestAction,
  WalletAlreadyExistsError,
} from '@root/entities/wallet/atoms/wallet-requests'
import {
  requestLocalPasswordAction,
  ensureStorageIsInitializedAction,
} from '@root/features/secure-storage/atoms'
import { refreshDeviceWalletsAction } from '@root/features/secure-storage/atoms'
import {
  WalletAlreadyExistsError as WalletAlreadyExistsOnDeviceError,
  WrongPasswordError,
} from '@root/features/secure-storage/secure-storage-error'
import { type WalletKind } from '@root/features/secure-storage/secure-storage-service'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { CancelError, Error } from '@root/shared'
import { showSnackbarAction } from '@root/shared/ui-kit'
import { delugeTheme } from '@root/shared/ui-kit/theme'

import type { Redacted } from '@blade/std-lib'
import type { Network } from '@root/entities/network'
import type { ApiError } from '@root/shared/api'

const colors = [
  delugeTheme.palette.acid[9],
  delugeTheme.palette.cyan[9],
  delugeTheme.palette.amber[9],
  delugeTheme.palette.green[9],
  delugeTheme.palette.orange[9],
  delugeTheme.palette.pink[9],
  delugeTheme.palette.purple[9],
]

const createOrGetDeviceWalletAction = action(
  async (
    ctx,
    {
      password,
      secret,
      network,
    }: {
      password: Redacted.Redacted
      secret:
        | { privateKey: Redacted.Redacted }
        | { mnemonic: Redacted.Redacted<Array<string>> }
      network: Network.Network
    },
  ) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)
    const kind: WalletKind = 'privateKey' in secret ? 'privateKey' : 'mnemonic'

    const deviceWalletResult = await ctx.schedule(() =>
      secureStorageService.importWallet(
        kind,
        'privateKey' in secret ? secret.privateKey : secret.mnemonic,
        password,
        network,
      ),
    )

    if (Result.isSuccess(deviceWalletResult)) {
      return deviceWalletResult
    }

    const error = deviceWalletResult.failure
    if (error instanceof WalletAlreadyExistsOnDeviceError) {
      return Result.succeed(error.wallet)
    }

    return Result.fail(error)
  },
  'createOrGetDeviceWalletAction',
)

export const reatomImportWallet = (opts?: { onImported?: () => void }) => {
  const importWalletStateAtom = atom(
    AsyncStateN.notInitiated<Error.UnknownError | ApiError.ApiError, unknown>(),
    'importWalletStateAtom',
  )

  const importWalletAction = action(
    async (
      ctx,
      args: {
        secret:
          | { privateKey: Redacted.Redacted }
          | { mnemonic: Redacted.Redacted<Array<string>> }
        name: string
      },
    ) => {
      const { secret, name } = args
      const network = ctx.get(selectedNetworkAtom)

      importWalletStateAtom(ctx, AsyncStateN.loading())

      const processDeviceWallet = async () => {
        const passwordResult = await ctx.schedule(() =>
          ensureStorageIsInitializedAction(ctx),
        )

        if (Result.isFailure(passwordResult)) {
          return Result.fail(passwordResult.failure)
        }

        const password = passwordResult.success.password
        if (password) {
          return createOrGetDeviceWalletAction(ctx, {
            password,
            secret,
            network,
          })
        }

        return requestLocalPasswordAction(ctx, async (ctx, { password }) => {
          return createOrGetDeviceWalletAction(ctx, {
            password,
            secret,
            network,
          })
        })
      }

      const deviceWalletResult = pipe(
        await ctx.schedule(() => processDeviceWallet()),
        (r) => Result.unify(r),
      )

      const kind: WalletKind =
        'privateKey' in secret ? 'privateKey' : 'mnemonic'

      if (Result.isFailure(deviceWalletResult)) {
        if (
          deviceWalletResult.failure instanceof WrongPasswordError ||
          deviceWalletResult.failure instanceof CancelError
        ) {
          importWalletStateAtom(ctx, AsyncStateN.notInitiated())
          return
        }

        showSnackbarAction(ctx, {
          text: i18next.t('wallet.import.error'),
          description:
            kind === 'privateKey'
              ? i18next.t('wallet.import.invalid-private-key')
              : i18next.t('wallet.import.invalid-seed-phrase'),
          intent: 'error',
          delay: 5000,
        })
        return
      }

      const deviceWallet = deviceWalletResult.success

      const walletStoreResult = await ctx.schedule(() =>
        storeWalletRequestAction(ctx, {
          network,
          walletId: deviceWallet.walletId,
          walletAddress: deviceWallet.address,
          color: Array.randomElement(colors)!,
          name,
        }),
      )

      batch(ctx, () => {
        const mappedResult = walletStoreResult.pipe(
          Result.mapError(Error.UnknownError.fallbackToUnknown),
        )
        importWalletStateAtom(ctx, AsyncStateN.fromResult(mappedResult))

        if (Result.isSuccess(walletStoreResult)) {
          refreshDeviceWalletsAction(ctx)
          showSnackbarAction(ctx, {
            text: i18next.t('wallet.import.success'),
            intent: 'success',
          })
          opts?.onImported?.()
        } else {
          importWalletStateAtom(ctx, AsyncStateN.notInitiated())

          if (walletStoreResult.failure instanceof WalletAlreadyExistsError) {
            showSnackbarAction(ctx, {
              text: i18next.t('wallet.import.already-exists'),
              intent: 'error',
              delay: 5000,
            })
            return
          }

          showSnackbarAction(ctx, {
            text: i18next.t('wallet.import.error'),
            intent: 'error',
          })
        }
      })
    },
    'importWalletAction',
  )

  return {
    importWalletStateAtom,
    importWalletAction,
  }
}
